'use client';

import { useMergeRefs } from '@floating-ui/react';
import {
  type ComponentProps,
  type MouseEventHandler,
  forwardRef,
  useCallback,
  useContext,
} from 'react';

import { cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { DrawerRootContext } from './context/drawer-root.context';
import { useDrawerRegistry } from './hooks/use-drawer-registry';
import type { DrawerTriggerPropsInterface } from './types/drawer-trigger.types';

/**
 * @since 10.22.0
 */
export const DrawerTrigger = forwardRef<HTMLButtonElement, DrawerTriggerPropsInterface>(
  (props: DrawerTriggerPropsInterface, forwardedRef) => {
    const { children, multiDrawerTriggerId, onClick, ...rest } = props;
    const { open } = useDrawerRegistry();
    const { drawer } = useContext(DrawerRootContext);

    // Drawer could be undefined if using multi drawer triggers
    const mergedRefs = useMergeRefs([drawer?.refs.setReference, forwardedRef]);

    // When multi drawer trigger id is used, open drawer via registry
    const onClickCallback = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        open(multiDrawerTriggerId, event);
        onClick?.(event);
      },
      [multiDrawerTriggerId, open, onClick],
    );

    let explicitTriggerProps: ComponentProps<'button'>;
    if (multiDrawerTriggerId) {
      explicitTriggerProps = { ...rest, onClick: onClickCallback };
    } else {
      const mergedReferenceProps = drawer.getReferenceProps(rest) as ComponentProps<'button'>;
      const {
        'aria-controls': _ariaControls,
        'aria-expanded': _ariaExpanded,
        'aria-haspopup': _ariaHasPopup,
        ...referenceProps
      } = mergedReferenceProps;

      explicitTriggerProps = referenceProps;
    }

    return (
      <Slot
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Drawer.Trigger"
        // Conditionally swap ref, props, and handlers between drawer registry and floating UI
        ref={multiDrawerTriggerId ? forwardedRef : mergedRefs}
        {...explicitTriggerProps}
        className={cx('c11n_drawer__trigger', explicitTriggerProps.className)}
      >
        {children}
      </Slot>
    );
  },
);

DrawerTrigger.displayName = 'Drawer.Trigger';
